import {
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import {
  formatDollarAmount,
  important,
  validateSchema,
} from '../../../utils/global/helper';
import { getInputBorderStyle } from '../../../helpers/styles';
import { getSplitPaymentAmountSchema } from '../../../types/payments';
import useSplitPayments from '../../../hooks/payments/useSplitPayments';
import {
  calculateFeeAmount,
  getPaymentMethodFee,
} from '../../../helpers/payments';
import { CheckoutPaymentMethod } from '../../../types/appointments';
import useUser from '../../../hooks/global/useUser';

interface Props {
  value: string;
  onChange: (value: string) => void;
  remainingAmount: number;
  totalPriceCents: number;
  disabled?: boolean;
  paymentMethod: CheckoutPaymentMethod;
}

function AmountInput({
  value,
  onChange,
  remainingAmount,
  totalPriceCents,
  disabled,
  paymentMethod,
}: Props) {
  const { user } = useUser();
  const { paymentMethods, currentIndex } = useSplitPayments();

  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const theme = useTheme();

  const validationSchema = getSplitPaymentAmountSchema(
    totalPriceCents,
    paymentMethods,
    currentIndex,
  );
  const { isValid, errors } = validateSchema(validationSchema, value);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsTouched(true);
    setIsFocused(false);

    // any number and ONLY ONE PERIOD
    const filteredValue = e.target.value.replace(/[^0-9.]/g, '');
    const periodCount = (filteredValue.match(/\./g) || []).length;
    if (periodCount > 1) {
      e.target.value = filteredValue.replace(/\.+/, '.');
    }
    if (e.target.value.endsWith('.')) {
      e.target.value = e.target.value.slice(0, -1);
    }
    onChange(e.target.value);
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const numberValue = !Number.isNaN(Number(value)) ? Number(value) : 0;
  const feeRate = paymentMethod
    ? getPaymentMethodFee(paymentMethod, user?.business)
    : 0;
  const feeAmount = calculateFeeAmount(numberValue, feeRate);
  const totalAmount = numberValue + feeAmount;

  const error = !isValid && isTouched;

  return (
    <Stack
      gap={0.25}
      sx={{
        '& div.Mui-disabled': {
          bgcolor: theme.palette.lightGrey.main,
        },
        '& input.Mui-disabled, & .MuiSelect-select.Mui-disabled': {
          bgcolor: important('transparent'),
        },
      }}>
      <TextField
        label="Payment amount"
        variant="outlined"
        prefix="$"
        placeholder={formatDollarAmount(remainingAmount, true, true)}
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        sx={{
          fontFamily: theme.typography.fontFamily,
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            isError: error,
            theme,
          }),
        }}
        slotProps={{
          input: {
            disabled,
            startAdornment:
              isFocused || !!value.length ? (
                <InputAdornment
                  sx={{
                    mr: 0,
                  }}
                  position="start">
                  $
                </InputAdornment>
              ) : null,
          },
          htmlInput: {
            sx: {
              fontSize: theme.typography.body2.fontSize,
              color: theme.palette.medGrey.main,
              px: 2,
              py: 3,
            },
          },
          inputLabel: {
            sx: {
              color: theme.palette.medGrey.main,
              lineHeight: 1.25,
            },
          },
        }}
      />
      {feeAmount > 0 && isValid && (
        <FormHelperText sx={{ color: theme.palette.medGrey.main }}>
          <Typography
            variant="inherit"
            fontWeight="bold"
            component="span"
            color="inherit">
            Total of {formatDollarAmount(totalAmount)}
          </Typography>{' '}
          will be charged to the customer&apos;s payment method
        </FormHelperText>
      )}
      {error && errors.length > 0 && (
        <FormHelperText sx={{ color: theme.palette.error.main }}>
          {errors[0]}
        </FormHelperText>
      )}
    </Stack>
  );
}

export default AmountInput;
